// What the transport needs from iroh, as plain interfaces: the real implementation wraps the
// WebAssembly build (load.ts); tests use an in-memory one (memory.ts).

/** How to reach an endpoint: its id (public key) and the relay it's connected to. */
export interface IrohAddress {
  endpointId: string;
  relayUrl: string;
}

/** One bidirectional QUIC stream: ordered, reliable bytes each way. */
export interface IrohStream {
  /** Resolves once the data is accepted by the send buffer (flow control applies). */
  write(data: Uint8Array): Promise<void>;
  /** Up to `max` bytes; `undefined` once the other side finished the stream. */
  read(max: number): Promise<Uint8Array | undefined>;
  /** Ends the send side; the other side's reads return `undefined` after what was written. */
  finish(): Promise<void>;
  /** Abandons the stream both ways (the other side gets an error). */
  abort(code?: number): Promise<void>;
}

/** A connection to one other endpoint. Authenticated: `remoteId` is who you're talking to. */
export interface IrohConnection {
  readonly remoteId: string;
  openStream(): Promise<IrohStream>;
  /** The next stream the other side opened. Rejects once the connection is closed. */
  acceptStream(): Promise<IrohStream>;
  close(code?: number, reason?: string): void;
  /** Resolves with the reason once the connection is closed, by either side or by a loss. */
  readonly closed: Promise<string>;
}

/** This browser's identity on the relay network. */
export interface IrohEndpoint {
  readonly id: string;
  /** The relay this endpoint is reachable through. */
  readonly relayUrl: string;
  connect(address: IrohAddress): Promise<IrohConnection>;
  /** The next incoming connection; `undefined` once the endpoint is closed. */
  accept(): Promise<IrohConnection | undefined>;
  close(): Promise<void>;
}

/** Creates an endpoint on one of the given relays (the closest that answers). */
export type IrohBinder = (relayUrls: string[]) => Promise<IrohEndpoint>;

/** 32 bytes of Ed25519 public key, as iroh prints it (64 hex digits). */
export const ENDPOINT_ID = /^[0-9a-f]{64}$/;

export function isEndpointId(value: unknown): value is string {
  return typeof value === "string" && ENDPOINT_ID.test(value);
}

/** Relays are reached over HTTPS (WebSocket). Plain HTTP only for local test relays. */
export function isRelayUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    const url = new URL(value);
    if (url.protocol === "https:") return true;
    return url.protocol === "http:" && ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname);
  } catch {
    return false;
  }
}
